import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import {
  MapPin, Plus, Search, Building2, Trash2, Edit, X, Save,
  Phone, Mail, Globe, ExternalLink, Loader2, Upload, Image, UploadCloud,
  ChevronDown, ChevronUp, Check, AlertCircle, Star, Copy, Heart, Clock,
  User, Briefcase, Users, ShoppingBag, FileText,
  CreditCard, Scale, Lock, Unlock
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
// Public places service (for making places public)
import { createPlace as createPublicPlace, updatePlace, linkPlaceToPeople, unlinkPlaceFromPeople, getPlaceRelation, getAllPlaces } from '../../services/placesService';
// Private places service (localStorage + encrypted file)
import {
  getUserPlaces,
  addUserPlace,
  addPlaceFromGoogle,
  updateUserPlace,
  removeUserPlace,
  makePublicPlace,
  setPlaceOwnership,
  searchUserPlaces,
  clearPlacesCache,
  togglePlaceFavorite
} from '../../services/userPlacesService';
import { getStoredUser as getUser } from '../../context/AuthContext';
import { getPlacesTypes, getPlacesTypesGrouped, categoryLabels } from '../../services/placesTypesService';
import { searchByVatNumber, searchByName, getPlaceTypeFromNace } from '../../services/cbeService';
import { uploadFile } from '../../services/filesService';
import { generatePlaceDescription, isGeminiAvailable } from '../../services/geminiService';
import { AddressAutocomplete, ImageUpload, CustomDropdown, Button, Modal, ConfirmModal, PageHeader } from '../ui';
import {
  searchPlaces as searchGooglePlacesService,
  getPlaceDetails,
  buildGoogleMapsUrl
} from '../../services/googlePlacesService';
import { countries, searchCountries, getPhonePrefix, getCountryByCode } from '../../utils/countries';
import { validatePlaceForm, hasErrors, isValidEmail, isValidPhone, isValidUrl, ensureUrlProtocol, normalizePhoneNumber } from '../../utils/validators';

const EndroitsPage = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const lang = language?.substring(0, 2) || 'fr';
  const user = getUser();

  // State
  const [places, setPlaces] = useState([]);
  const [placesTypes, setPlacesTypes] = useState([]);
  const [groupedTypes, setGroupedTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [placeFilter, setPlaceFilter] = useState('all'); // 'all', 'owners', 'members', 'favorites', 'public'
  const [publicPlaces, setPublicPlaces] = useState([]);
  const [publicSearch, setPublicSearch] = useState('');
  const [publicLoading, setPublicLoading] = useState(false);
  const [publicSearched, setPublicSearched] = useState(false);
  const [duplicateCheckResults, setDuplicateCheckResults] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null); // Category filter
  const [selectedTypes, setSelectedTypes] = useState([]); // Types within category
  const [formCategory, setFormCategory] = useState(''); // Category in form
  const [showForm, setShowForm] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showGoogleUrlAlert, setShowGoogleUrlAlert] = useState(false);
  const [makePublicConfirm, setMakePublicConfirm] = useState(null); // Confirm make public modal
  const [generatingDescription, setGeneratingDescription] = useState(false); // AI description generation


  // Get userId from localStorage
  const userId = parseInt(localStorage.getItem('userId'), 10);

  // Check if user is admin
  let isAdmin = false;
  try {
    const cabazaUser = JSON.parse(localStorage.getItem('cabaza_user') || '{}');
    isAdmin = cabazaUser.isAdmin === true || cabazaUser.isAdmin === 'true' || cabazaUser.isAdmin === 1 || cabazaUser.isAdmin === '1';
  } catch (e) {
    console.error('Error parsing cabaza_user:', e);
  }

  // BCE Search state
  const [selectedCountry, setSelectedCountry] = useState('BE');
  const [bceMode, setBceMode] = useState('vat'); // 'vat' or 'name'
  const [searchVat, setSearchVat] = useState('');
  const [searchName, setSearchName] = useState('');
  const [bceSearching, setBceSearching] = useState(false);
  const [bceError, setBceError] = useState('');
  const [bceResult, setBceResult] = useState(null);
  const [bceNameResults, setBceNameResults] = useState([]);
  const [bceResultsHidden, setBceResultsHidden] = useState(false); // To hide/show results

  // Google Places autocomplete state
  const [placesSuggestions, setPlacesSuggestions] = useState([]);
  const [placesSearching, setPlacesSearching] = useState(false);
  const [showPlacesSuggestions, setShowPlacesSuggestions] = useState(false);
  const placesAutocompleteRef = useRef(null);
  const placesServiceRef = useRef(null);

  // Relationship types (labels will be translated in render)
  const RELATIONSHIP_TYPES = [
    { value: 'owner', labelKey: 'relationOwner', icon: 'Briefcase' },
    { value: 'employee', labelKey: 'relationEmployee', icon: 'User' },
    { value: 'worker', labelKey: 'relationWorker', icon: 'Users' },
    { value: 'client', labelKey: 'relationClient', icon: 'ShoppingBag' },
    { value: 'visitor', labelKey: 'relationVisitor', icon: 'MapPin' }
  ];

  // Form state
  const [formData, setFormData] = useState({
    placeName: '',
    placeTypeCode: '',
    // Address fields
    googlePlaceId: '',
    street: '',
    number: '',
    box: '',
    postalCode: '',
    city: '',
    country: '',
    countryCode: '',
    latitude: null,
    longitude: null,
    // Contact info
    email: '',
    phone: '',
    website: '',
    contactName: '',
    description: '',
    tvaNum: '',
    bankNum: '',
    firmType: '',
    facebookUrl: '',
    twitterUrl: '',
    youtubeUrl: '',
    googleUrl: '', // URL vers la page Google de l'établissement
    parking: false,
    status: true,
    relationshipType: '', // Relation avec l'établissement
    addToFavorites: false, // Ajouter aux favoris
    isOwner: false, // Je suis le propriétaire de cet établissement
    isMember: false // Membre de cet établissement
  });

  // Google Places selected result
  const [googlePlaceResult, setGooglePlaceResult] = useState(null);
  const [googlePlacePhotos, setGooglePlacePhotos] = useState([]);
  const [copiedFields, setCopiedFields] = useState({}); // Track which fields have been copied

  // Form validation and helpers
  const [formErrors, setFormErrors] = useState({});
  const [websiteProtocol, setWebsiteProtocol] = useState('https://');
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Filtered countries for autocomplete
  const filteredCountries = countrySearch
    ? searchCountries(countrySearch)
    : countries;

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [localPlaces, typesData, groupedData] = await Promise.all([
        userId ? getUserPlaces(userId) : Promise.resolve([]),
        getPlacesTypes({ lang, active: true }),
        getPlacesTypesGrouped(lang)
      ]);

      const places = (Array.isArray(localPlaces) ? localPlaces : []).map(p => ({
        ...p,
        placeName: p.placeName || p.placename || '',
        placeUuid: (p.placeUuid || p.placeuuid || '').toUpperCase(),
        placeTypeCode: p.placeTypeCode || p.placetypecode || '',
        isFavorite: !!(p.isFavorite ?? p.isfavorite),
        isOwner: !!(p.isOwner ?? p.isowner),
        isMember: !!(p.isMember ?? p.ismember),
        category: p.placeTypeCode || p.placetypecode || p.category || '',
        role: p.role || 'CONTACT',
      }));

      setPlaces(places);
      const types = Array.isArray(typesData) ? typesData : (typesData?.data || typesData?.DATA || []);
      setPlacesTypes(types);
      setGroupedTypes(groupedData?.categories || groupedData?.CATEGORIES || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlaceRelationFromCache = (placeUuid) => {
    if (!placeUuid) return { isFavorite: false, isOwner: false, isMember: false, linked: false };
    const uuid = placeUuid.toUpperCase();
    const cached = places.find(p => (p.placeUuid || '').toUpperCase() === uuid);
    if (!cached) return { isFavorite: false, isOwner: false, isMember: false, linked: false };
    return { isFavorite: !!cached.isFavorite, isOwner: !!cached.isOwner, isMember: !!cached.isMember, linked: true };
  };

  // Search public places
  const handlePublicSearch = async () => {
    setPublicLoading(true);
    setPublicSearched(true);
    try {
      const results = await getAllPlaces({ search: publicSearch, limit: 20 });
      setPublicPlaces(results || []);
    } catch (err) {
      console.error('Error searching public places:', err);
      setPublicPlaces([]);
    } finally {
      setPublicLoading(false);
    }
  };

  const handlePublicPlaceAction = async (publicPlace, flag) => {
    const placeUuid = publicPlace.placeUuid || publicPlace.PLACEUUID || publicPlace.placeuuid;
    const relation = getPlaceRelationFromCache(placeUuid);

    if (relation.linked && relation[flag]) {
      const newRelation = { ...relation, [flag]: false };
      const hasOtherFlags = newRelation.isFavorite || newRelation.isOwner || newRelation.isMember;
      if (!hasOtherFlags) {
        await unlinkPlaceFromPeople(userId, placeUuid);
      } else {
        await addUserPlace(userId, {
          ...publicPlace,
          placeUuid,
          role: 'PUBLIC',
          isFavorite: newRelation.isFavorite,
          isOwner: newRelation.isOwner,
          isMember: newRelation.isMember,
        });
      }
    } else {
      await addUserPlace(userId, {
        ...publicPlace,
        placeUuid,
        role: 'PUBLIC',
        isFavorite: flag === 'isFavorite' ? true : (relation.isFavorite || false),
        isOwner: flag === 'isOwner' ? true : (relation.isOwner || false),
        isMember: flag === 'isMember' ? true : (relation.isMember || false),
      });
    }
    await loadData();
  };

  // Handle relationship type change - sync isMember/isOwner
  const handleRelationshipChange = (value) => {
    const memberRoles = ['employee', 'worker', 'member'];
    const ownerRoles = ['owner'];
    setFormData(prev => ({
      ...prev,
      relationshipType: value,
      isMember: memberRoles.includes(value),
      isOwner: ownerRoles.includes(value)
    }));
  };

  // Get icon component
  const getIcon = (iconName, className = "w-5 h-5") => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className={className} /> : <Building2 className={className} />;
  };

  // Get unique categories from types
  const categories = [...new Set(placesTypes.map(t => t.category))].filter(Boolean);

  // Get types for a specific category
  const getTypesForCategory = (category) => {
    return placesTypes.filter(t => t.category === category);
  };

  // Filter places
  const filteredPlaces = places.filter(place => {
    const matchesSearch = !searchTerm ||
      place.placeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      place.street?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      place.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      place.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Place type filter (owners, members, favorites, public)
    let matchesPlaceFilter = true;
    if (placeFilter === 'owners') {
      matchesPlaceFilter = !!place.isOwner;
    } else if (placeFilter === 'members') {
      matchesPlaceFilter = !!place.isMember;
    } else if (placeFilter === 'favorites') {
      matchesPlaceFilter = !!place.isFavorite || place.category === 'favorite' || place.placeTypeCode === 'favorite';
    }
    // 'all' et 'public' → pas de filtre sur places[]

    if (!matchesPlaceFilter) return false;

    // If no category selected, show all
    if (!selectedFilterCategory) return matchesSearch;

    // If category selected but no specific types deselected, show all from category
    const categoryTypes = getTypesForCategory(selectedFilterCategory);
    const activeTypes = categoryTypes.filter(ct => !selectedTypes.find(st => st.id === ct.id && st.excluded));

    const matchesTypes = activeTypes.some(type =>
      type.code === place.placeTypeCode
    );

    return matchesSearch && matchesTypes;
  });

  // Count for filter buttons
  const ownersCount = places.filter(p => !!p.isOwner).length;
  const membersCount = places.filter(p => !!p.isMember).length;
  const favoritesCount = places.filter(p => !!p.isFavorite || p.category === 'favorite' || p.placeTypeCode === 'favorite').length;

  // Select a category filter
  const handleSelectFilterCategory = (category) => {
    setSelectedFilterCategory(category);
    setSelectedTypes([]); // Reset type exclusions
  };

  // Toggle type exclusion (deselect/reselect)
  const handleToggleTypeFilter = (type) => {
    const existing = selectedTypes.find(t => t.id === type.id);
    if (existing) {
      setSelectedTypes(selectedTypes.filter(t => t.id !== type.id));
    } else {
      setSelectedTypes([...selectedTypes, { ...type, excluded: true }]);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedFilterCategory(null);
    setSelectedTypes([]);
  };

  // Exclude all types in category
  const handleExcludeAllTypes = () => {
    const categoryTypes = getTypesForCategory(selectedFilterCategory);
    setSelectedTypes(categoryTypes.map(t => ({ ...t, excluded: true })));
  };

  // Include all types in category
  const handleIncludeAllTypes = () => {
    setSelectedTypes([]);
  };

  // BCE Search by VAT number
  const handleSearchByVat = async () => {
    if (!searchVat.trim()) return;

    setBceSearching(true);
    setBceError('');
    setBceResult(null);
    setBceNameResults([]);

    try {
      const result = await searchByVatNumber(searchVat);
      if (result.success) {
        setBceResult(result.data);
      } else {
        setBceError(result.message || 'Entreprise non trouvée');
      }
    } catch (err) {
      setBceError(err.message);
    } finally {
      setBceSearching(false);
    }
  };

  // BCE Search by name
  const handleSearchByNameCBE = async () => {
    if (!searchName.trim() || searchName.length < 2) {
      setBceError('Veuillez entrer au moins 2 caractères');
      return;
    }

    setBceSearching(true);
    setBceError('');
    setBceNameResults([]);
    setBceResult(null);

    try {
      const result = await searchByName(searchName);
      if (result.success && result.data?.length > 0) {
        setBceNameResults(result.data);
      } else if (result.success && result.data?.length === 0) {
        setBceError('Aucune entreprise trouvée');
      } else {
        setBceError(result.message || 'Erreur lors de la recherche');
      }
    } catch (err) {
      setBceError(err.message);
    } finally {
      setBceSearching(false);
    }
  };

  // Select company from BCE name search results
  const handleSelectFromBce = (company) => {
    setBceResult(company);
    setBceNameResults([]);
  };

  // Apply BCE result to form
  const applyBceResult = () => {
    if (!bceResult) return;

    // Set category if detected
    if (bceResult.detectedCategory) {
      setFormCategory(bceResult.detectedCategory);
    }

    const newPlaceName = bceResult.placeName || '';

    setFormData(prev => ({
      ...prev,
      placeName: newPlaceName || prev.placeName,
      tvaNum: bceResult.tvaNum || prev.tvaNum,
      firmType: bceResult.firmType || prev.firmType,
      phone: bceResult.phone || prev.phone,
      email: bceResult.email || prev.email,
      website: bceResult.website || prev.website
    }));
    setBceResult(null);
    setSearchVat('');
    setSearchName('');

    // Trigger Google Places search with the BCE name
    if (newPlaceName && newPlaceName.length >= 3) {
      setShowPlacesSuggestions(true);
      searchGooglePlaces(newPlaceName);
    }
  };

  // Autocomplete BCE - search automatically after 3 characters (name) or 6 characters (VAT)
  useEffect(() => {
    if (selectedCountry !== 'BE' || !showForm) return;

    const timer = setTimeout(() => {
      if (bceMode === 'name' && searchName.length >= 3) {
        handleSearchByNameCBE();
      } else if (bceMode === 'vat' && searchVat.replace(/[\s.]/g, '').length >= 6) {
        handleSearchByVat();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchName, searchVat, bceMode, selectedCountry, showForm]);

  // Handle address selection
  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      googlePlaceId: addressData.place_id || '',
      street: addressData.street || addressData.route || '',
      number: addressData.number || addressData.street_number || '',
      postalCode: addressData.postalCode || addressData.postal_code || '',
      city: addressData.city || addressData.locality || '',
      country: addressData.country || '',
      countryCode: addressData.countryCode || addressData.country_code || '',
      latitude: addressData.lat,
      longitude: addressData.lng
    }));
  };

  // Google Places autocomplete search using service
  const searchGooglePlaces = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setPlacesSuggestions([]);
      return;
    }

    setPlacesSearching(true);
    try {
      const results = await searchGooglePlacesService(query, {
        regionCodes: selectedCountry ? [selectedCountry.toLowerCase()] : ['be', 'fr', 'nl', 'de', 'lu'],
        types: ['establishment'],
        maxResults: 8
      });

      if (results && results.length > 0) {
        // Convert to format compatible with our UI
        const formattedSuggestions = results.map(result => ({
          place_id: result.placeId,
          description: result.description,
          main_text: result.name,
          secondary_text: result.secondaryText
        }));

        setPlacesSuggestions(formattedSuggestions);
        setShowPlacesSuggestions(true);
      } else {
        setPlacesSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching places:', error);
      setPlacesSuggestions([]);
    } finally {
      setPlacesSearching(false);
    }
  }, [selectedCountry]);

  // Select a place from Google suggestions using new Place API
  const handleSelectGooglePlace = useCallback(async (placeId) => {
    if (!window.google?.maps?.places?.Place) return;

    try {
      const place = new window.google.maps.places.Place({ id: placeId });

      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'internationalPhoneNumber', 'nationalPhoneNumber', 'websiteURI', 'types', 'addressComponents', 'photos', 'googleMapsURI', 'rating', 'userRatingCount', 'regularOpeningHours', 'businessStatus', 'priceLevel']
      });

      // Extended mapping: Google type -> { category, typeCode }
      // typeCode corresponds to Cabaza place types codes
      const googleTypeToCabaza = {
        // Food & Dining
        restaurant: { category: 'food', typeCode: 'restaurant' },
        cafe: { category: 'food', typeCode: 'cafe' },
        bakery: { category: 'food', typeCode: 'bakery' },
        bar: { category: 'food', typeCode: 'bar' },
        meal_takeaway: { category: 'food', typeCode: 'fastfood' },
        meal_delivery: { category: 'food', typeCode: 'fastfood' },
        food: { category: 'food', typeCode: 'restaurant' },
        // Shopping
        store: { category: 'shopping', typeCode: 'store' },
        shopping_mall: { category: 'shopping', typeCode: 'mall' },
        clothing_store: { category: 'shopping', typeCode: 'clothing' },
        supermarket: { category: 'shopping', typeCode: 'supermarket' },
        grocery_or_supermarket: { category: 'shopping', typeCode: 'supermarket' },
        convenience_store: { category: 'shopping', typeCode: 'convenience' },
        electronics_store: { category: 'shopping', typeCode: 'electronics' },
        furniture_store: { category: 'shopping', typeCode: 'furniture' },
        home_goods_store: { category: 'shopping', typeCode: 'home' },
        jewelry_store: { category: 'shopping', typeCode: 'jewelry' },
        book_store: { category: 'shopping', typeCode: 'books' },
        florist: { category: 'shopping', typeCode: 'florist' },
        pet_store: { category: 'shopping', typeCode: 'pets' },
        // Health
        hospital: { category: 'health', typeCode: 'hospital' },
        doctor: { category: 'health', typeCode: 'doctor' },
        pharmacy: { category: 'health', typeCode: 'pharmacy' },
        dentist: { category: 'health', typeCode: 'dentist' },
        physiotherapist: { category: 'health', typeCode: 'physio' },
        veterinary_care: { category: 'health', typeCode: 'vet' },
        beauty_salon: { category: 'health', typeCode: 'beauty' },
        hair_care: { category: 'health', typeCode: 'hairdresser' },
        spa: { category: 'health', typeCode: 'spa' },
        // Sports & Leisure
        gym: { category: 'sports', typeCode: 'gym' },
        stadium: { category: 'sports', typeCode: 'stadium' },
        bowling_alley: { category: 'sports', typeCode: 'bowling' },
        // Entertainment
        movie_theater: { category: 'entertainment', typeCode: 'cinema' },
        night_club: { category: 'entertainment', typeCode: 'nightclub' },
        casino: { category: 'entertainment', typeCode: 'casino' },
        amusement_park: { category: 'entertainment', typeCode: 'amusement' },
        museum: { category: 'entertainment', typeCode: 'museum' },
        art_gallery: { category: 'entertainment', typeCode: 'gallery' },
        // Hospitality
        lodging: { category: 'hospitality', typeCode: 'hotel' },
        hotel: { category: 'hospitality', typeCode: 'hotel' },
        campground: { category: 'hospitality', typeCode: 'camping' },
        // Education
        school: { category: 'education', typeCode: 'school' },
        university: { category: 'education', typeCode: 'university' },
        library: { category: 'education', typeCode: 'library' },
        // Services
        bank: { category: 'services', typeCode: 'bank' },
        accounting: { category: 'services', typeCode: 'accounting' },
        lawyer: { category: 'services', typeCode: 'lawyer' },
        insurance_agency: { category: 'services', typeCode: 'insurance' },
        real_estate_agency: { category: 'services', typeCode: 'realestate' },
        travel_agency: { category: 'services', typeCode: 'travel' },
        car_repair: { category: 'services', typeCode: 'garage' },
        car_dealer: { category: 'services', typeCode: 'cardealer' },
        gas_station: { category: 'services', typeCode: 'gasstation' },
        laundry: { category: 'services', typeCode: 'laundry' },
        // Transport
        airport: { category: 'transport', typeCode: 'airport' },
        train_station: { category: 'transport', typeCode: 'trainstation' },
        bus_station: { category: 'transport', typeCode: 'busstation' },
        taxi_stand: { category: 'transport', typeCode: 'taxi' },
        parking: { category: 'transport', typeCode: 'parking' },
        // Government
        local_government_office: { category: 'government', typeCode: 'government' },
        city_hall: { category: 'government', typeCode: 'cityhall' },
        courthouse: { category: 'government', typeCode: 'courthouse' },
        police: { category: 'government', typeCode: 'police' },
        fire_station: { category: 'government', typeCode: 'firestation' },
        post_office: { category: 'government', typeCode: 'postoffice' },
        // Social
        church: { category: 'social', typeCode: 'church' },
        mosque: { category: 'social', typeCode: 'mosque' },
        synagogue: { category: 'social', typeCode: 'synagogue' },
        // Business
        point_of_interest: { category: 'business', typeCode: 'enterprise' },
        establishment: { category: 'business', typeCode: 'enterprise' }
      };

      let detectedCategory = 'business';
      let detectedTypeCode = null;
      let googleTypes = [];

      if (place.types) {
        googleTypes = [...place.types];
        for (const type of place.types) {
          if (googleTypeToCabaza[type]) {
            detectedCategory = googleTypeToCabaza[type].category;
            detectedTypeCode = googleTypeToCabaza[type].typeCode;
            break;
          }
        }
      }

      // Set form category
      setFormCategory(detectedCategory);

      // Try to find and select the matching type in Cabaza
      if (detectedTypeCode) {
        const matchingType = placesTypes.find(t =>
          t.code === detectedTypeCode && t.category === detectedCategory
        );
        if (matchingType) {
          setFormData(prev => ({
            ...prev,
            placeTypeCode: matchingType.code
          }));
        }
      }

      // Get Google Maps URL
      const googleUrl = place.googleMapsURI || `https://www.google.com/maps/place/?q=place_id:${placeId}`;

      // Get photos URLs (max 6)
      const photos = [];
      if (place.photos && place.photos.length > 0) {
        for (let i = 0; i < Math.min(6, place.photos.length); i++) {
          const photo = place.photos[i];
          if (photo.getURI) {
            photos.push(photo.getURI({ maxWidth: 400, maxHeight: 300 }));
          }
        }
      }
      setGooglePlacePhotos(photos);

      // Parse opening hours
      let openingHours = null;
      if (place.regularOpeningHours?.weekdayDescriptions) {
        openingHours = place.regularOpeningHours.weekdayDescriptions;
      }

      // Extract address components
      let street = '', streetNumber = '', city = '', postalCode = '', country = '', countryCode = '';
      if (place.addressComponents) {
        for (const component of place.addressComponents) {
          if (component.types.includes('route') || component.types.includes('street_address')) {
            street = component.longText || component.shortText;
          }
          if (component.types.includes('street_number')) {
            streetNumber = component.longText || component.shortText;
          }
          if (component.types.includes('locality')) {
            city = component.longText || component.shortText;
          }
          if (component.types.includes('postal_code')) {
            postalCode = component.longText || component.shortText;
          }
          if (component.types.includes('country')) {
            country = component.longText; // Full country name
            countryCode = component.shortText; // Country code like 'BE', 'FR'
          }
        }
      }

      // Store the selected place result for display (all info for user to copy)
      setGooglePlaceResult({
        name: place.displayName,
        address: place.formattedAddress,
        phone: place.nationalPhoneNumber || place.internationalPhoneNumber,
        internationalPhone: place.internationalPhoneNumber,
        website: place.websiteURI,
        googleUrl: googleUrl,
        rating: place.rating,
        ratingCount: place.userRatingCount,
        category: detectedCategory,
        detectedTypeCode: detectedTypeCode,
        googleTypes: googleTypes,
        photos: photos,
        openingHours: openingHours,
        businessStatus: place.businessStatus,
        priceLevel: place.priceLevel,
        editorialSummary: place.editorialSummary, // Google's description of the place
        street: street,
        number: streetNumber,
        city: city,
        postalCode: postalCode,
        country: country,
        countryCode: countryCode,
        latitude: place.location?.lat(),
        longitude: place.location?.lng()
      });

      // Get phone and website from place
      const phone = place.nationalPhoneNumber || place.internationalPhoneNumber || '';
      const website = place.websiteURI || '';

      // Reset copied fields tracker - mark all auto-copied fields
      setCopiedFields({
        placeName: true,
        street: true,
        city: true,
        googleUrl: true,
        coordinates: true,
        phone: !!phone,
        website: !!website
      });

      // Auto-fill name, address components, coordinates, googleUrl, phone and website
      setFormData(prev => ({
        ...prev,
        placeName: place.displayName || prev.placeName,
        googlePlaceId: placeId,
        street: street || prev.street,
        number: streetNumber || prev.number,
        postalCode: postalCode || prev.postalCode,
        city: city || prev.city,
        country: country || prev.country,
        countryCode: countryCode || prev.countryCode,
        latitude: place.location?.lat() || prev.latitude,
        longitude: place.location?.lng() || prev.longitude,
        googleUrl: googleUrl,
        phone: phone || prev.phone,
        website: website || prev.website
      }));

      // Update country if detected
      if (countryCode) {
        setSelectedCountry(countryCode);
      }

      // Clear suggestions
      setPlacesSuggestions([]);
      setShowPlacesSuggestions(false);
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  }, [placesTypes]);

  // Debounced Google Places search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.placeName && showPlacesSuggestions) {
        searchGooglePlaces(formData.placeName);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.placeName, searchGooglePlaces, showPlacesSuggestions]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Generate description with AI
  const handleGenerateDescription = async () => {
    if (!googlePlaceResult && !formData.placeName) {
      return;
    }

    setGeneratingDescription(true);
    try {
      const placeData = {
        name: formData.placeName || googlePlaceResult?.name,
        category: googlePlaceResult?.category || formData.placeTypeCode,
        address: googlePlaceResult?.address || `${formData.street} ${formData.number}, ${formData.postalCode} ${formData.city}`.trim(),
        city: formData.city || googlePlaceResult?.city,
        country: formData.country || googlePlaceResult?.country,
        website: formData.website || googlePlaceResult?.website,
        googleTypes: googlePlaceResult?.googleTypes,
        editorialSummary: googlePlaceResult?.editorialSummary // Google's description
      };

      const description = await generatePlaceDescription(placeData, lang);
      setFormData(prev => ({ ...prev, description }));
    } catch (error) {
      console.error('Error generating description:', error);
    } finally {
      setGeneratingDescription(false);
    }
  };

  // Handle type selection
  const handleTypeSelect = (type) => {
    setFormData(prev => ({
      ...prev,
      placeTypeCode: type.code
    }));
  };

  // Open create form
  const handleCreate = () => {
    setEditingPlace(null);
    setFormCategory(''); // Reset form category
    setGooglePlaceResult(null); // Reset Google Places result
    setGooglePlacePhotos([]); // Reset photos
    setShowGoogleUrlAlert(false); // Reset alert
    setFormData({
      placeName: '',
      placeTypeCode: '',
      googlePlaceId: '',
      street: '',
      number: '',
      box: '',
      postalCode: '',
      city: '',
      country: '',
      countryCode: '',
      latitude: null,
      longitude: null,
      email: '',
      phone: '',
      website: '',
      contactName: '',
      description: '',
      tvaNum: '',
      bankNum: '',
      firmType: '',
      facebookUrl: '',
      twitterUrl: '',
      youtubeUrl: '',
      googleUrl: '',
      parking: false,
      status: true,
      relationshipType: '',
      addToFavorites: false,
      isOwner: false,
      isMember: false
    });
    setShowForm(true);
  };

  // Open edit form
  const handleEdit = async (place) => {
    setEditingPlace(place);
    setGooglePlaceResult(null); // Reset Google Places result
    setGooglePlacePhotos([]); // Reset photos
    setShowGoogleUrlAlert(false); // Reset alert
    // Find the category of the current type
    const currentType = placesTypes.find(t => t.code === place.placeTypeCode || t.code === place.category);
    setFormCategory(currentType?.category || place.category || '');

    setFormData({
      placeName: place.placeName || '',
      placeTypeCode: place.placeTypeCode || place.category || 'favorite',
      googlePlaceId: place.googlePlaceId || '',
      street: place.street || '',
      number: place.number || '',
      box: place.box || '',
      postalCode: place.postalCode || '',
      city: place.city || '',
      country: place.country || '',
      countryCode: place.countryCode || '',
      latitude: place.latitude,
      longitude: place.longitude,
      email: place.email || '',
      phone: place.phone || '',
      website: place.website || '',
      contactName: place.contactName || '',
      description: place.description || '',
      tvaNum: place.tvaNum || '',
      bankNum: place.bankNum || '',
      firmType: place.firmType || '',
      facebookUrl: place.facebookUrl || '',
      twitterUrl: place.twitterUrl || '',
      youtubeUrl: place.youtubeUrl || '',
      googleUrl: place.googleUrl || '',
      parking: place.parking || false,
      status: place.status !== false,
      relationshipType: place.relationshipType || (place.isOwner ? 'owner' : ''),
      addToFavorites: place.isFavorite || false,
      isOwner: place.isOwner || false
    });
    setShowForm(true);
  };

  // Open edit form with alert for missing Google URL
  const handleEditWithAlert = async (place) => {
    await handleEdit(place);
    setShowGoogleUrlAlert(true);

    // Try to auto-fetch Google URL if we have address and Google Maps is loaded
    const searchAddress = [place.street, place.number, place.city].filter(Boolean).join(' ');
    if (searchAddress && window.google?.maps?.places?.AutocompleteSuggestion) {
      try {
        const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: `${place.placeName} ${searchAddress}`,
          includedPrimaryTypes: ['establishment'],
          includedRegionCodes: ['be', 'fr', 'nl', 'de', 'lu']
        });

        if (suggestions && suggestions.length > 0) {
          const placeId = suggestions[0].placePrediction?.placeId;
          if (placeId) {
            const placeObj = new window.google.maps.places.Place({ id: placeId });
            await placeObj.fetchFields({ fields: ['googleMapsURI'] });

            if (placeObj.googleMapsURI) {
              setFormData(prev => ({ ...prev, googleUrl: placeObj.googleMapsURI }));
              // Auto-save the googleUrl
              await updatePlace(place.id, { googleUrl: placeObj.googleMapsURI });
              console.log('Google URL auto-fetched and saved:', placeObj.googleMapsURI);
            }
          }
        }
      } catch (error) {
        console.error('Error auto-fetching Google URL:', error);
      }
    }
  };

  // Actual save logic (extracted for reuse after duplicate check)
  const doSave = async (data) => {
    setSaving(true);
    try {
      let result;

      // Prepare place data
      const isMemberVal = data.isMember || ['employee', 'worker', 'member'].includes(data.relationshipType || '');
      const placeData = {
        ...data,
        isOwner: data.isOwner || data.relationshipType === 'owner',
        isMember: isMemberVal,
        isFavorite: data.addToFavorites,
        category: formCategory || 'favorite'
      };

      if (editingPlace) {
        // Update existing private place
        result = await updateUserPlace(userId, editingPlace.localUuid || editingPlace.localId, placeData);
        if (result) {
          setShowForm(false);
          loadData();
        } else {
          alert(t.common?.error || 'Erreur lors de la mise à jour');
        }
      } else {
        // If isOwner is checked, create public place first then link
        if (data.isOwner || data.relationshipType === 'owner') {
          // Create public place in DB
          const publicPlaceData = {
            placeName: data.placeName,
            googlePlaceId: data.googlePlaceId || '',
            street: data.street || '',
            number: data.number || '',
            box: data.box || '',
            postalCode: data.postalCode || '',
            city: data.city || '',
            country: data.country || '',
            countryCode: data.countryCode || '',
            latitude: data.latitude,
            longitude: data.longitude,
            phone: data.phone || '',
            email: data.email || '',
            website: data.website || '',
            description: data.description || '',
            placeTypeCode: formCategory || 'enterprise',
            tvaNum: data.tvaNum || '',
            googleUrl: data.googleUrl || '',
            createdById: userId
          };

          const publicResult = await createPublicPlace(publicPlaceData);

          if (publicResult.success && publicResult.data) {
            const placeUuid = publicResult.data.placeUuid || publicResult.data.PLACEUUID;

            // Save to local storage with placeUuid (addUserPlace does POST /places/link)
            placeData.placeUuid = placeUuid;
            placeData.publicPlaceId = publicResult.data.id || publicResult.data.ID;
            placeData.role = 'PUBLIC';
            placeData.isOwner = true;
            placeData.isMember = false;
            placeData.isFavorite = data.addToFavorites || false;
            result = await addUserPlace(userId, placeData);
          } else {
            alert(publicResult.message || 'Erreur lors de la creation publique');
            setSaving(false);
            return;
          }
        } else {
          // Create private place only
          result = await addUserPlace(userId, placeData);
        }

        if (result) {
          setShowForm(false);
          loadData();
        } else {
          alert(t.common?.error || 'Erreur lors de la creation');
        }
      }
    } catch (error) {
      console.error('Error saving place:', error);
      alert(t.common?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // Save place (private - localStorage + encrypted file, or public if isOwner)
  const handleSave = async () => {
    if (!formData.placeName) {
      alert(t.endroits?.nameRequired || 'Le nom est requis');
      return;
    }

    // Check for duplicates only on creation
    if (!editingPlace) {
      try {
        const existingPublic = await getAllPlaces({ search: formData.placeName, limit: 5 });
        const similar = (existingPublic || []).filter(p =>
          p.placeName?.toLowerCase().includes(formData.placeName.toLowerCase()) ||
          formData.placeName.toLowerCase().includes((p.placeName || '').toLowerCase())
        );
        if (similar.length > 0) {
          setDuplicateCheckResults(similar);
          setPendingSaveData({ ...formData });
          setShowDuplicateModal(true);
          return;
        }
      } catch (err) {
        console.warn('Duplicate check failed, continuing save:', err);
      }
    }

    await doSave(formData);
  };

  // Link an existing public place to the user's profile
  const handleLinkExistingPublic = async (publicPlace) => {
    setShowDuplicateModal(false);
    setSaving(true);
    try {
      const isMemberVal = pendingSaveData?.isMember || ['employee', 'worker', 'member'].includes(pendingSaveData?.relationshipType || '');
      const localPlace = {
        ...(pendingSaveData || {}),
        placeUuid: publicPlace.placeUuid,
        placeName: publicPlace.placeName,
        role: 'PUBLIC',
        isOwner: pendingSaveData?.isOwner || false,
        isMember: isMemberVal,
        isFavorite: pendingSaveData?.addToFavorites || false,
        category: formCategory || 'favorite'
      };
      await addUserPlace(userId, localPlace);
      // Reload places
      const updatedPlaces = await getUserPlaces(userId);
      setPlaces(updatedPlaces || []);
      setShowForm(false);
      setPendingSaveData(null);
      setDuplicateCheckResults([]);
      loadData();
    } catch (err) {
      console.error('Link error:', err);
      alert(t.common?.error || 'Erreur lors de la liaison');
    } finally {
      setSaving(false);
    }
  };

  // Create private place anyway (ignoring duplicate)
  const handleCreatePrivateAnyway = async () => {
    setShowDuplicateModal(false);
    const data = pendingSaveData;
    setPendingSaveData(null);
    setDuplicateCheckResults([]);
    await doSave(data);
  };

  // Delete place (private)
  const handleDelete = async (place) => {
    setDeleteConfirm(place);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      const success = await removeUserPlace(userId, deleteConfirm.localUuid || deleteConfirm.localId);
      if (success) {
        loadData();
      } else {
        alert(t.common?.error || 'Erreur');
      }
    } catch (error) {
      console.error('Error deleting place:', error);
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Handle make public request
  const handleMakePublic = (place) => {
    if (!place.isOwner) {
      // Ask user if they are the owner
      setMakePublicConfirm(place);
    } else {
      // Already marked as owner, proceed to make public
      confirmMakePublic(place);
    }
  };

  // Confirm and make place public
  const confirmMakePublic = async (place = makePublicConfirm) => {
    if (!place) return;

    try {
      // First, mark as owner if not already
      if (!place.isOwner) {
        await setPlaceOwnership(userId, place.localUuid || place.localId, true);
      }

      // Then make public
      const result = await makePublicPlace(userId, place.localUuid || place.localId);
      if (result.success) {
        alert('Lieu rendu public avec succès !');
        loadData();
      } else {
        alert(result.message || 'Erreur lors de la publication');
      }
    } catch (error) {
      console.error('Error making place public:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setMakePublicConfirm(null);
    }
  };

  // Get type info for a place
  const getTypeInfo = (place) => {
    const type = placesTypes.find(t => t.code === place.placeTypeCode);
    return type || { icon: 'Building2', color: '#6366f1', name: place.placeTypeCode || t.endroits?.defaultType || 'Business' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <PageHeader
        moduleCode="PLACES"
        title={t.endroits?.title || 'Endroits'}
        subtitle={t.endroits?.subtitle || 'Gérez vos lieux : entreprises, clubs, restaurants...'}
      >
        <Button onClick={handleCreate} icon={Plus}>
          {t.endroits?.add || 'Ajouter un endroit'}
        </Button>
      </PageHeader>

      {/* Filters */}
      <div className="space-y-4 mb-6">
        {/* Place type filter buttons */}
        <div className="flex flex-wrap gap-2">
          {/* Tous */}
          <button
            onClick={() => setPlaceFilter('all')}
            className={`inline-flex items-center gap-2 px-4 py-2 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
              placeFilter === 'all'
                ? 'bg-gray-700 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <MapPin className="w-4 h-4" />
            {t.endroits?.all || 'Tous'}
            <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${placeFilter === 'all' ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {places.length}
            </span>
          </button>

          {/* Proprietaires */}
          <button
            onClick={() => setPlaceFilter('owners')}
            className={`inline-flex items-center gap-2 px-4 py-2 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
              placeFilter === 'owners'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50'
            }`}
          >
            <Building2 className="w-4 h-4" />
            {t.endroits?.myOwnerPlaces || 'Proprietaires'}
            <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${placeFilter === 'owners' ? 'bg-white/20' : 'bg-blue-200 dark:bg-blue-800'}`}>
              {ownersCount}
            </span>
          </button>

          {/* Membres */}
          <button
            onClick={() => setPlaceFilter('members')}
            className={`inline-flex items-center gap-2 px-4 py-2 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
              placeFilter === 'members'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50'
            }`}
          >
            <Users className="w-4 h-4" />
            {t.endroits?.myMemberPlaces || 'Membres'}
            <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${placeFilter === 'members' ? 'bg-white/20' : 'bg-purple-200 dark:bg-purple-800'}`}>
              {membersCount}
            </span>
          </button>

          {/* Favoris */}
          <button
            onClick={() => setPlaceFilter('favorites')}
            className={`inline-flex items-center gap-2 px-4 py-2 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
              placeFilter === 'favorites'
                ? 'bg-pink-600 text-white shadow-md'
                : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900/50'
            }`}
          >
            <Heart className="w-4 h-4" />
            {t.endroits?.myFavorites || 'Favoris'}
            <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${placeFilter === 'favorites' ? 'bg-white/20' : 'bg-pink-200 dark:bg-pink-800'}`}>
              {favoritesCount}
            </span>
          </button>

          {/* Endroits publics */}
          <button
            onClick={() => setPlaceFilter('public')}
            className={`inline-flex items-center gap-2 px-4 py-2 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
              placeFilter === 'public'
                ? 'bg-emerald-600 text-white shadow-md'
                : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
            }`}
          >
            <Globe className="w-4 h-4" />
            {t.endroits?.publicPlaces || 'Endroits publics'}
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t.common?.search || 'Rechercher...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          {/* Category filter dropdown */}
          <CustomDropdown
            value={selectedFilterCategory || ''}
            onChange={(val) => handleSelectFilterCategory(val || null)}
            options={[
              { value: '', label: t.endroits?.categories?.all || 'Toutes les catégories' },
              ...categories.map(cat => ({
                value: cat,
                label: t.endroits?.categories?.[cat] || cat
              }))
            ]}
            className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:border-purple-400 focus:ring-2 focus:ring-purple-500 min-w-[200px]"
          />
        </div>

        {/* Selected category tag + types */}
        {selectedFilterCategory && (
          <div className="space-y-3">
            {/* Category tag with actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
              >
                {t.endroits?.categories?.[selectedFilterCategory] || selectedFilterCategory}
                <button
                  onClick={handleClearFilters}
                  className="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                  title={t.endroits?.clearCategory || 'Retirer la catégorie'}
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
              {/* Toggle all types buttons */}
              <button
                onClick={handleExcludeAllTypes}
                className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title={t.endroits?.deselectAll || 'Tout désélectionner'}
              >
                {t.endroits?.deselectAll || 'Tout désélectionner'}
              </button>
              <button
                onClick={handleIncludeAllTypes}
                className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title={t.endroits?.selectAll || 'Tout sélectionner'}
              >
                {t.endroits?.selectAll || 'Tout sélectionner'}
              </button>
            </div>

            {/* Types within category - clickable to exclude/include */}
            <div className="flex flex-wrap gap-2">
              {getTypesForCategory(selectedFilterCategory).map(type => {
                const isExcluded = selectedTypes.find(t => t.id === type.id && t.excluded);
                return (
                  <button
                    key={type.id}
                    onClick={() => handleToggleTypeFilter(type)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isExcluded
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
                        : 'text-white'
                    }`}
                    style={!isExcluded ? { backgroundColor: type.color || '#6366f1' } : {}}
                  >
                    {getIcon(type.icon, "w-4 h-4")}
                    {type.name}
                    {isExcluded && <X className="w-3.5 h-3.5 ml-1" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Places Grid */}
      {placeFilter === 'public' ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={publicSearch}
              onChange={(e) => setPublicSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePublicSearch()}
              placeholder={t.endroits?.publicPlacesSearchPlaceholder || 'Rechercher un endroit public...'}
              className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <Button
              onClick={handlePublicSearch}
              disabled={publicLoading}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {publicLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          {!publicSearched && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              {t.endroits?.publicPlacesSearchHelp || 'Tapez un nom pour rechercher dans les endroits publics de la plateforme'}
            </p>
          )}
          {publicSearched && !publicLoading && publicPlaces.length === 0 && (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <MapPin className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">{t.endroits?.noPublicPlacesFound || 'Aucun endroit public trouve'}</p>
            </div>
          )}
          {publicSearched && publicPlaces.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {publicPlaces.map(place => (
                <div
                  key={place.placeUuid || place.id}
                  onClick={() => navigate(`/endroits/${place.placeUuid}`)}
                  className="rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-400"
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100 line-clamp-1">{place.placeName}</h3>
                      <span className="px-2 py-0.5 text-xs bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 rounded-full whitespace-nowrap">
                        {t.endroits?.publicBadge || 'Public'}
                      </span>
                    </div>
                    {(place.street || place.city) && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        {[place.street, place.number, place.city].filter(Boolean).join(' ')}
                      </p>
                    )}
                    {place.placeTypeCode && (
                      <span className="mt-2 inline-block px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                        {place.placeTypeCode}
                      </span>
                    )}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      {(() => {
                        const uuid = (place.placeUuid || place.PLACEUUID || place.placeuuid || '');
                        const rel = getPlaceRelationFromCache(uuid);
                        return (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePublicPlaceAction(place, 'isFavorite'); }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                                rel.isFavorite
                                  ? 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400'
                                  : 'bg-gray-100 text-gray-500 hover:bg-pink-50 hover:text-pink-500 dark:bg-gray-700 dark:text-gray-400'
                              }`}
                              title={rel.isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                            >
                              <Heart className={`w-3.5 h-3.5 ${rel.isFavorite ? 'fill-pink-500' : ''}`} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePublicPlaceAction(place, 'isMember'); }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                                rel.isMember
                                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                  : 'bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-500 dark:bg-gray-700 dark:text-gray-400'
                              }`}
                              title={rel.isMember ? 'Retirer des membres' : 'Je suis membre'}
                            >
                              <Users className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePublicPlaceAction(place, 'isOwner'); }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                                rel.isOwner
                                  ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-gray-100 text-gray-500 hover:bg-yellow-50 hover:text-yellow-500 dark:bg-gray-700 dark:text-gray-400'
                              }`}
                              title={rel.isOwner ? 'Retirer propriétaire' : 'Je suis propriétaire'}
                            >
                              <Building2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : filteredPlaces.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <MapPin className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm
              ? (t.endroits?.noResults || 'Aucun resultat trouve')
              : (t.endroits?.empty || 'Aucun endroit enregistre')}
          </p>
          <button
            onClick={handleCreate}
            className="mt-4 text-purple-500 hover:text-purple-600 font-medium"
          >
            {t.endroits?.addFirst || 'Ajouter votre premier endroit'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPlaces.map(place => {
            const typeInfo = getTypeInfo(place);
            const isPrivate = place.role === 'CONTACT' || !place.publicPlaceId;
            const isPublic = place.role === 'PUBLIC' && place.publicPlaceId;
            const canMakePublic = isPrivate && place.isOwner;
            return (
              <div
                key={place.localUuid || place.localId || place.id}
                className={`rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
                  isPublic
                    ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-400'
                    : place.isOwner
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400'
                    : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700'
                }`}
                onClick={() => navigate(`/endroits/${place.localUuid || place.localId || place.placeUuid}`)}
              >
                {/* Card Header with Type + Favorite */}
                <div
                  className="px-4 py-3 flex items-center gap-3"
                  style={{ backgroundColor: `${typeInfo.color}15` }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: typeInfo.color }}
                  >
                    {getIcon(typeInfo.icon, "w-5 h-5 text-white")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                      {place.placeName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {typeInfo.name}
                    </p>
                  </div>
                  {/* Private/Public badge */}
                  {isPrivate ? (
                    <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full flex items-center gap-1">
                      <Lock className="w-3 h-3" /> {t.endroits?.private || 'Privé'}
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full flex items-center gap-1">
                      <Unlock className="w-3 h-3" /> {t.endroits?.public || 'Public'}
                    </span>
                  )}
                  {/* Owner badge */}
                  {place.isOwner && (
                    <span className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-full">
                      {t.endroits?.owner || 'Propriétaire'}
                    </span>
                  )}
                  {/* Rating stars if available */}
                  {place.totalReviews > 0 && (
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="text-gray-600 dark:text-gray-400">
                        {(place.totalStars / place.totalReviews).toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Card Content */}
                <div className="p-4 space-y-2">
                  {(place.street || place.city) && (
                    <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                      <span className="line-clamp-2">
                        {[place.street, place.number, place.box].filter(Boolean).join(' ')}
                        {(place.street || place.number) && (place.postalCode || place.city) && ', '}
                        {[place.postalCode, place.city].filter(Boolean).join(' ')}
                      </span>
                    </div>
                  )}
                  {place.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Phone className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span>{place.phone}</span>
                    </div>
                  )}
                  {place.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Mail className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span className="truncate">{place.email}</span>
                    </div>
                  )}
                  {place.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(place.website.startsWith('http') ? place.website : `https://${place.website}`, '_blank');
                        }}
                        className="text-purple-500 hover:text-purple-600 truncate flex items-center gap-1 cursor-pointer"
                      >
                        {place.website.replace(/^https?:\/\//, '')}
                        <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  )}
                  {place.tvaNum && (
                    <div className="text-xs text-gray-400 mt-2">
                      TVA: {place.tvaNum}
                    </div>
                  )}

                </div>

                {/* Card Actions + Created by */}
                <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    {/* Created by + Date */}
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      {place.createdByPeopleId ? (
                        place.createdByPeopleId === userId ? (
                          <span>{user?.firstName || user?.lastName || t.endroits?.you || 'Vous'}</span>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.dispatchEvent(new CustomEvent('openPeopleDetail', { detail: { peopleId: place.createdByPeopleId } }));
                            }}
                            className="hover:text-purple-500 hover:underline"
                          >
                            {place.createdByName || `#${place.createdByPeopleId}`}
                          </button>
                        )
                      ) : (
                        <span>{t.endroits?.you || 'Vous'}</span>
                      )}
                      <span>•</span>
                      <span>{place.createdAt && new Date(place.createdAt).toLocaleDateString(lang)}</span>
                    </div>
                    {/* Actions */}
                    <div className="flex gap-1">
                      {/* Favorite toggle */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const localId = place.localUuid || place.localId;
                          const placeUuid = place.placeUuid;
                          const newFav = !place.isFavorite;
                          // Optimistic UI update
                          setPlaces(prev => prev.map(p =>
                            (p === place) ? { ...p, isFavorite: newFav } : p
                          ));
                          if (localId) {
                            // Private place: toggle in localStorage
                            togglePlaceFavorite(userId, localId).then(result => {
                              if (result === null) {
                                // Revert on error
                                setPlaces(prev => prev.map(p => (p === place) ? { ...p, isFavorite: !newFav } : p));
                              }
                            });
                          } else if (placeUuid) {
                            // Public place (API-only): toggle via API
                            const apiCall = newFav
                              ? linkPlaceToPeople(userId, placeUuid, 'PUBLIC', true)
                              : unlinkPlaceFromPeople(userId, placeUuid);
                            apiCall.catch(() => {
                              setPlaces(prev => prev.map(p => (p === place) ? { ...p, isFavorite: !newFav } : p));
                            });
                          }
                        }}
                        className={`p-2 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 inline-flex items-center justify-center rounded-lg transition-colors ${place.isFavorite ? 'text-pink-500 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-900/20' : 'text-gray-400 hover:text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20'}`}
                        title={place.isFavorite ? (t.endroits?.removeFromFavorites || 'Retirer des favoris') : (t.endroits?.addToFavorites || 'Ajouter aux favoris')}
                      >
                        <Heart className={`w-4 h-4 ${place.isFavorite ? 'fill-pink-500' : ''}`} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(place);
                        }}
                        className="p-2 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 inline-flex items-center justify-center text-gray-500 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                        title={t.common?.edit || 'Modifier'}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {/* Delete button - only if user is the creator */}
                      {(!place.createdByPeopleId || place.createdByPeopleId === userId) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(place);
                          }}
                          className="p-2 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 inline-flex items-center justify-center text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title={t.common?.delete || 'Supprimer'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingPlace ? (t.endroits?.edit || 'Modifier l\'endroit') : (t.endroits?.create || 'Nouvel endroit')}
        icon={MapPin}
        size="lg"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowForm(false)}
            >
              {t.common?.cancel || 'Annuler'}
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              icon={Save}
            >
              {t.common?.save || 'Enregistrer'}
            </Button>
          </>
        }
      >
              <div className="space-y-6">
                {/* Country Selection with Autocomplete */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <span className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-purple-500" />
                      {t.endroits?.country || 'Pays'}
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={countrySearch || getCountryByCode(selectedCountry)?.name || ''}
                      onChange={(e) => {
                        setCountrySearch(e.target.value);
                        setShowCountryDropdown(true);
                      }}
                      onFocus={() => setShowCountryDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                      placeholder="Rechercher un pays..."
                      className="w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg">
                      {getCountryByCode(selectedCountry)?.flag || '🌍'}
                    </span>
                  </div>
                  {showCountryDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg max-h-60 overflow-y-auto">
                      {filteredCountries.slice(0, 50).map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => {
                            setSelectedCountry(country.code);
                            setCountrySearch('');
                            setShowCountryDropdown(false);
                            setBceResult(null);
                            setBceNameResults([]);
                            setBceError('');
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center gap-3 ${
                            selectedCountry === country.code ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                          }`}
                        >
                          <span className="text-lg">{country.flag}</span>
                          <span className="flex-1 text-gray-900 dark:text-white">{country.name}</span>
                          <span className="text-xs text-gray-500">{country.phonePrefix}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* BCE Search for Belgium */}
                {selectedCountry === 'BE' && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 space-y-4">
                    <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                      <Building2 className="w-5 h-5" />
                      <span className="font-medium">{t.endroits?.bceTitle || 'Recherche BCE (Banque Carrefour des Entreprises)'}</span>
                    </div>

                    {/* Mode tabs: by VAT or by Name */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setBceMode('vat');
                          setBceError('');
                          setBceNameResults([]);
                        }}
                        className={`flex-1 py-2 px-4 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
                          bceMode === 'vat'
                            ? 'bg-purple-500 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {t.endroits?.byVat || 'Par N° TVA'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setBceMode('name');
                          setBceError('');
                          setBceResult(null);
                        }}
                        className={`flex-1 py-2 px-4 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
                          bceMode === 'name'
                            ? 'bg-purple-500 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {t.endroits?.byName || 'Par nom'}
                      </button>
                    </div>

                    {/* Search by VAT number */}
                    {bceMode === 'vat' && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={searchVat}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSearchVat(value);
                            if (!value.trim()) {
                              // Clear results when field is emptied
                              setBceResult(null);
                              setBceNameResults([]);
                            }
                            setBceResultsHidden(false);
                          }}
                          onFocus={() => setBceResultsHidden(false)} // Show results on focus
                          placeholder={t.endroits?.vatPlaceholder || 'Ex: 0123.456.789 ou BE0123456789'}
                          className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                          onKeyDown={(e) => e.key === 'Enter' && handleSearchByVat()}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setBceResultsHidden(false); // Show results
                            if (bceResult || bceNameResults.length > 0) return; // Already have results
                            handleSearchByVat();
                          }}
                          disabled={bceSearching || !searchVat.trim()}
                          className="px-4 py-2 min-h-11 sm:min-h-0 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {bceSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </button>
                      </div>
                    )}

                    {/* Search by name */}
                    {bceMode === 'name' && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={searchName}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSearchName(value);
                            if (!value.trim()) {
                              // Clear results when field is emptied
                              setBceResult(null);
                              setBceNameResults([]);
                            }
                            setBceResultsHidden(false);
                          }}
                          onFocus={() => setBceResultsHidden(false)} // Show results on focus
                          placeholder="Nom de l'entreprise..."
                          className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                          onKeyDown={(e) => e.key === 'Enter' && handleSearchByNameCBE()}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setBceResultsHidden(false); // Show results
                            if (bceResult || bceNameResults.length > 0) return; // Already have results
                            handleSearchByNameCBE();
                          }}
                          disabled={bceSearching || !searchName.trim()}
                          className="px-4 py-2 min-h-11 sm:min-h-0 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {bceSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </button>
                      </div>
                    )}

                    {/* Error message */}
                    {bceError && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        {bceError}
                      </div>
                    )}

                    {/* Name search results list */}
                    {bceNameResults.length > 0 && !bceResultsHidden && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        {/* Header with close button */}
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {bceNameResults.length} résultat{bceNameResults.length > 1 ? 's' : ''}
                          </span>
                          <button
                            type="button"
                            onClick={() => setBceResultsHidden(true)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                            title="Fermer les résultats"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                        {/* Results list */}
                        <div className="max-h-60 overflow-y-auto">
                          {bceNameResults.map((result, index) => {
                            const ResultIcon = result.detectedIcon ? LucideIcons[result.detectedIcon] : Building2;
                            return (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handleSelectFromBce(result)}
                                className="w-full p-3 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                                    {ResultIcon && <ResultIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white truncate">{result.placeName}</p>
                                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                      <span>{result.tvaNum}</span>
                                      {result.detectedCategory && (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                          {t.endroits?.categories?.[result.detectedCategory] || result.detectedCategory}
                                        </span>
                                      )}
                                    </div>
                                    {result.city && (
                                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{result.zip} {result.city}</p>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Selected BCE result */}
                    {bceResult && !bceResultsHidden && (() => {
                      const SelectedIcon = bceResult.detectedIcon ? LucideIcons[bceResult.detectedIcon] : Building2;
                      // Find matching type for color
                      const matchingType = placesTypes.find(t =>
                        t.category === bceResult.detectedCategory || t.code === bceResult.detectedType
                      );
                      const typeColor = matchingType?.color || '#9333ea'; // Purple fallback
                      return (
                        <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-purple-500 overflow-hidden">
                          {/* Close button */}
                          <div className="flex justify-end px-2 py-1">
                            <button
                              type="button"
                              onClick={() => setBceResultsHidden(true)}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Fermer"
                            >
                              <X className="w-4 h-4 text-gray-400" />
                            </button>
                          </div>
                          <div className="px-4 pb-4">
                            <div className="flex items-start gap-3">
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                style={{ backgroundColor: typeColor }}
                              >
                                {SelectedIcon && <SelectedIcon className="w-5 h-5 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 dark:text-white">{bceResult.placeName}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-sm text-purple-600 dark:text-purple-400">{bceResult.tvaNum}</p>
                                  {bceResult.detectedCategory && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                      {t.endroits?.categories?.[bceResult.detectedCategory] || bceResult.detectedCategory}
                                    </span>
                                  )}
                                </div>
                                {bceResult.city && (
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{bceResult.zip} {bceResult.city}</p>
                                )}
                                {bceResult.firmType && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{bceResult.firmType}</p>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={applyBceResult}
                              className="mt-3 w-full py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
                            >
                              <Check className="w-4 h-4" />
                              Utiliser ces données
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t.endroits?.bceAutoFill || 'Les données seront récupérées automatiquement via la Banque Carrefour des Entreprises'}
                    </p>
                  </div>
                )}

                {/* Category & Type Selection */}
                <div className="space-y-4">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-purple-500" />
                        {t.endroits?.category || 'Catégorie'} *
                      </span>
                    </label>
                    <CustomDropdown
                      value={formCategory}
                      onChange={(val) => {
                        setFormCategory(val);
                        // Reset type selection when category changes
                        setFormData(prev => ({ ...prev, placeTypeCode: '' }));
                      }}
                      options={[
                        { value: '', label: t.endroits?.selectCategory || '-- Sélectionner une catégorie --' },
                        ...categories.map(cat => ({
                          value: cat,
                          label: t.endroits?.categories?.[cat] || cat
                        }))
                      ]}
                      className="w-full px-3 py-2 border-2 border-purple-400 dark:border-purple-500 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:border-purple-600 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Type Selection - only show when category is selected */}
                  {formCategory && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <span className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-purple-500" />
                          {t.endroits?.type || 'Type d\'endroit'} *
                        </span>
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border-2 border-purple-400 dark:border-purple-500 rounded-lg">
                        {getTypesForCategory(formCategory).map(type => (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => handleTypeSelect(type)}
                            className={`flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors ${
                              formData.placeTypeCode === type.code
                                ? 'bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500'
                                : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-2 border-transparent'
                            }`}
                          >
                            <span
                              className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: type.color }}
                            >
                              {getIcon(type.icon, "w-4 h-4 text-white")}
                            </span>
                            <span className="truncate text-gray-700 dark:text-gray-300">{type.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-purple-500" />
                        {t.endroits?.name || 'Nom'} *
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="placeName"
                        value={formData.placeName}
                        onChange={(e) => {
                          handleInputChange(e);
                          setShowPlacesSuggestions(true);
                        }}
                        onFocus={() => setShowPlacesSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowPlacesSuggestions(false), 200)}
                        className="w-full px-3 py-2 border-2 border-purple-400 dark:border-purple-500 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white pr-10 focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                        placeholder={t.endroits?.namePlaceholder || 'Rechercher un établissement...'}
                        required
                      />
                      {placesSearching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                      )}
                    </div>

                    {/* Google Places suggestions dropdown */}
                    {showPlacesSuggestions && placesSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg max-h-60 overflow-y-auto">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Search className="w-3 h-3" />
                            Google Places
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowPlacesSuggestions(false)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                          >
                            <X className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                        {placesSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.place_id}
                            type="button"
                            onClick={() => handleSelectGooglePlace(suggestion.place_id)}
                            className="w-full p-3 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                                <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white truncate">
                                  {suggestion.main_text || suggestion.description}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {suggestion.secondary_text || ''}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Google Places Result Card with Photos and Copy Buttons */}
                {googlePlaceResult && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-blue-500 overflow-hidden">
                    {/* Photos carousel */}
                    {googlePlacePhotos.length > 0 && (
                      <div className="flex overflow-x-auto gap-1 bg-gray-100 dark:bg-gray-900">
                        {googlePlacePhotos.map((photoUrl, index) => (
                          <img
                            key={index}
                            src={photoUrl}
                            alt={`${googlePlaceResult.name} - ${index + 1}`}
                            className="h-32 w-auto object-cover flex-shrink-0"
                          />
                        ))}
                      </div>
                    )}
                    {/* Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-lg">{googlePlaceResult.name}</h4>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {googlePlaceResult.priceLevel && (
                              <span className="text-sm text-gray-500">
                                {'€'.repeat(googlePlaceResult.priceLevel)}
                              </span>
                            )}
                            {googlePlaceResult.businessStatus && googlePlaceResult.businessStatus !== 'OPERATIONAL' && (
                              <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-600">
                                {googlePlaceResult.businessStatus === 'CLOSED_TEMPORARILY' ? 'Fermé temporairement' : 'Fermé'}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setGooglePlaceResult(null);
                            setGooglePlacePhotos([]);
                            setCopiedFields({});
                          }}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>

                      {/* Copyable info section */}
                      <div className="mt-4 space-y-2 bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                        {/* Name - auto-copied */}
                        <div className={`flex items-center gap-2 p-2 rounded transition-colors ${
                          copiedFields.placeName
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                        }`}
                          onClick={() => {
                            if (!copiedFields.placeName) {
                              setFormData(prev => ({ ...prev, placeName: googlePlaceResult.name }));
                              setCopiedFields(prev => ({ ...prev, placeName: true }));
                            }
                          }}
                        >
                          <Building2 className={`w-4 h-4 flex-shrink-0 ${copiedFields.placeName ? 'text-green-500' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-500 w-16">Nom</span>
                          <span className={`flex-1 text-sm truncate ${copiedFields.placeName ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                            {googlePlaceResult.name}
                          </span>
                          {copiedFields.placeName ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3 text-gray-300" />
                          )}
                        </div>

                        {/* Address - auto-copied */}
                        {(googlePlaceResult.street || googlePlaceResult.city) && (
                          <div className={`flex items-center gap-2 p-2 rounded ${
                            copiedFields.street
                              ? 'bg-green-50 dark:bg-green-900/20'
                              : 'bg-gray-100 dark:bg-gray-800'
                          }`}>
                            <MapPin className={`w-4 h-4 flex-shrink-0 ${copiedFields.street ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">Adresse</span>
                            <span className={`flex-1 text-sm truncate ${copiedFields.street ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {[googlePlaceResult.street, googlePlaceResult.number].filter(Boolean).join(' ')}
                              {(googlePlaceResult.street || googlePlaceResult.number) && (googlePlaceResult.postalCode || googlePlaceResult.city) && ', '}
                              {[googlePlaceResult.postalCode, googlePlaceResult.city].filter(Boolean).join(' ')}
                              {googlePlaceResult.countryCode && ` (${googlePlaceResult.countryCode})`}
                            </span>
                            {copiedFields.street && <Check className="w-4 h-4 text-green-500" />}
                          </div>
                        )}

                        {/* Phone - auto-copied */}
                        {googlePlaceResult.phone && (
                          <div className={`flex items-center gap-2 p-2 rounded ${
                            copiedFields.phone
                              ? 'bg-green-50 dark:bg-green-900/20'
                              : 'bg-gray-100 dark:bg-gray-800'
                          }`}>
                            <Phone className={`w-4 h-4 flex-shrink-0 ${copiedFields.phone ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">Tél</span>
                            <span className={`flex-1 text-sm ${copiedFields.phone ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {googlePlaceResult.phone}
                            </span>
                            {copiedFields.phone && <Check className="w-4 h-4 text-green-500" />}
                          </div>
                        )}

                        {/* Website - auto-copied */}
                        {googlePlaceResult.website && (
                          <div className={`flex items-center gap-2 p-2 rounded ${
                            copiedFields.website
                              ? 'bg-green-50 dark:bg-green-900/20'
                              : 'bg-gray-100 dark:bg-gray-800'
                          }`}>
                            <Globe className={`w-4 h-4 flex-shrink-0 ${copiedFields.website ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="text-xs text-gray-500 w-16">Site web</span>
                            <span className={`flex-1 text-sm truncate ${copiedFields.website ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              {googlePlaceResult.website}
                            </span>
                            {copiedFields.website && <Check className="w-4 h-4 text-green-500" />}
                          </div>
                        )}

                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => window.open(googlePlaceResult.googleUrl, '_blank', 'width=800,height=600')}
                          className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Voir sur Google Maps
                        </button>
                      </div>
                      {/* Powered by Google */}
                      <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <span className="text-xs text-gray-400">Powered by</span>
                        <svg className="h-4" viewBox="0 0 272 92" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#EA4335" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
                          <path fill="#FBBC05" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
                          <path fill="#4285F4" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/>
                          <path fill="#34A853" d="M225 3v65h-9.5V3h9.5z"/>
                          <path fill="#EA4335" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/>
                          <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                {/* Address Fields */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t.endroits?.address || 'Adresse'}
                  </label>

                  {/* Street + Number + Box */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-7">
                      <input
                        type="text"
                        name="street"
                        value={formData.street}
                        onChange={handleInputChange}
                        placeholder={t.endroits?.street || 'Rue'}
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700"
                      />
                    </div>
                    <div className="col-span-3">
                      <input
                        type="text"
                        name="number"
                        value={formData.number}
                        onChange={handleInputChange}
                        placeholder={t.endroits?.number || 'N°'}
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700"
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="text"
                        name="box"
                        value={formData.box}
                        onChange={handleInputChange}
                        placeholder={t.endroits?.box || 'Bte'}
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700"
                      />
                    </div>
                  </div>

                  {/* Postal Code + City */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder={t.endroits?.postalCode || 'Code postal'}
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700"
                      />
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder={t.endroits?.city || 'Ville'}
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700"
                      />
                    </div>
                  </div>

                  {/* Map - using googlePlaceId or coordinates */}
                  {(formData.googlePlaceId || (formData.latitude && formData.longitude)) && (
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      <div className="h-48 w-full">
                        <iframe
                          title="Location map"
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          style={{ border: 0 }}
                          src={formData.googlePlaceId
                            ? `https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=place_id:${formData.googlePlaceId}`
                            : `https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${formData.latitude},${formData.longitude}&zoom=16`
                          }
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}

                  {/* Hidden fields for DB */}
                  <input type="hidden" name="googlePlaceId" value={formData.googlePlaceId || ''} />
                  <input type="hidden" name="googleUrl" value={formData.googleUrl || ''} />
                  <input type="hidden" name="latitude" value={formData.latitude || ''} />
                  <input type="hidden" name="longitude" value={formData.longitude || ''} />
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Email */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-purple-500" />
                        {t.common?.email || 'Email'}
                      </span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => {
                        handleInputChange(e);
                        // Clear error on change
                        if (formErrors.email) {
                          setFormErrors(prev => ({ ...prev, email: null }));
                        }
                      }}
                      onBlur={() => {
                        if (formData.email && !isValidEmail(formData.email)) {
                          setFormErrors(prev => ({ ...prev, email: 'Adresse email invalide' }));
                        }
                      }}
                      placeholder="contact@example.com"
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                        formErrors.email ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    />
                    <div className="absolute left-3 top-[2.1rem] text-gray-400">
                      <Mail className="w-4 h-4" />
                    </div>
                    {formErrors.email && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone with country prefix */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-purple-500" />
                        {t.endroits?.phone || 'Téléphone'}
                      </span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 py-2 rounded-l-lg border border-r-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm">
                        {getPhonePrefix(selectedCountry)}
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          handleInputChange(e);
                          if (formErrors.phone) {
                            setFormErrors(prev => ({ ...prev, phone: null }));
                          }
                        }}
                        onBlur={() => {
                          if (formData.phone && !isValidPhone(formData.phone)) {
                            setFormErrors(prev => ({ ...prev, phone: 'Numéro de téléphone invalide' }));
                          }
                        }}
                        placeholder="470 12 34 56"
                        className={`flex-1 px-3 py-2 border rounded-r-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                          formErrors.phone ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                        }`}
                      />
                    </div>
                    {formErrors.phone && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.phone}
                      </p>
                    )}
                  </div>

                  {/* Website with protocol selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-purple-500" />
                        {t.endroits?.website || 'Site web'}
                      </span>
                    </label>
                    <div className="flex">
                      <CustomDropdown
                        value={websiteProtocol}
                        onChange={(val) => setWebsiteProtocol(val)}
                        options={[
                          { value: 'https://', label: 'https://' },
                          { value: 'http://', label: 'http://' }
                        ]}
                        className="px-2 py-2 rounded-l-lg border border-r-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-sm"
                      />
                      <input
                        type="text"
                        name="website"
                        value={formData.website?.replace(/^https?:\/\//, '') || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Prepend protocol only if user starts typing
                          const fullUrl = value ? `${websiteProtocol}${value}` : '';
                          setFormData(prev => ({ ...prev, website: fullUrl }));
                          if (formErrors.website) {
                            setFormErrors(prev => ({ ...prev, website: null }));
                          }
                        }}
                        onBlur={() => {
                          if (formData.website && !isValidUrl(formData.website)) {
                            setFormErrors(prev => ({ ...prev, website: 'URL invalide' }));
                          }
                        }}
                        placeholder="www.example.com"
                        className={`flex-1 px-3 py-2 border rounded-r-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                          formErrors.website ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                        }`}
                      />
                    </div>
                    {formErrors.website && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {formErrors.website}
                      </p>
                    )}
                  </div>

                  {/* Contact person */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4 text-purple-500" />
                        {t.endroits?.contactName || 'Personne de contact'}
                      </span>
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      placeholder="Jean Dupont"
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Business Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* TVA Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-purple-500" />
                        {t.endroits?.tvaNum || 'Numéro TVA'}
                      </span>
                    </label>
                    <input
                      type="text"
                      name="tvaNum"
                      value={formData.tvaNum}
                      onChange={handleInputChange}
                      placeholder={selectedCountry === 'BE' ? 'Ex: BE 0123.456.789' : 'Ex: FR12345678901'}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      readOnly={selectedCountry === 'BE' && formData.tvaNum}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        <Scale className="w-4 h-4 text-purple-500" />
                        {t.endroits?.firmType || 'Forme juridique'}
                      </span>
                    </label>
                    <CustomDropdown
                      value={formData.firmType}
                      onChange={(val) => setFormData(prev => ({ ...prev, firmType: val }))}
                      options={[
                        { value: '', label: t.endroits?.select || '-- Sélectionner --' },
                        { value: 'SA', label: 'SA - Société Anonyme' },
                        { value: 'SRL', label: 'SRL - Société à Responsabilité Limitée' },
                        { value: 'SPRL', label: 'SPRL - Société Privée à Responsabilité Limitée' },
                        { value: 'SC', label: 'SC - Société Coopérative' },
                        { value: 'SNC', label: 'SNC - Société en Nom Collectif' },
                        { value: 'ASBL', label: 'ASBL - Association Sans But Lucratif' },
                        { value: 'Indépendant', label: 'Indépendant' },
                        { value: 'Autre', label: 'Autre' }
                      ]}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:border-purple-400"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-500" />
                        {t.endroits?.description || 'Description'}
                      </span>
                    </label>
                    {isGeminiAvailable() && (googlePlaceResult || formData.placeName) && (
                      <button
                        type="button"
                        onClick={handleGenerateDescription}
                        disabled={generatingDescription}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                      >
                        {generatingDescription ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Génération...
                          </>
                        ) : (
                          <>
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" fill="currentColor"/>
                            </svg>
                            IA Gemini
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none"
                    placeholder={googlePlaceResult ? "Cliquez sur 'IA Gemini' pour générer une description automatique" : ""}
                  />
                </div>


                {/* Relationship Type */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t.endroits?.relationshipType || 'Ma relation avec cet établissement'}
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {RELATIONSHIP_TYPES.map(rel => {
                        const RelIcon = LucideIcons[rel.icon] || User;
                        const isSelected = formData.relationshipType === rel.value;
                        return (
                          <button
                            key={rel.value}
                            type="button"
                            onClick={() => handleRelationshipChange(rel.value)}
                            className={`flex flex-col items-center gap-2 p-3 rounded-lg transition-all ${
                              isSelected
                                ? 'bg-purple-500 text-white shadow-md'
                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                            }`}
                          >
                            <RelIcon className="w-5 h-5" />
                            <span className="text-xs font-medium text-center">{t.endroits?.[rel.labelKey] || rel.labelKey}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Add to favorites - heart toggle */}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, addToFavorites: !prev.addToFavorites }))}
                    className={`flex items-center gap-3 w-full p-3 rounded-lg transition-all ${
                      formData.addToFavorites
                        ? 'bg-pink-100 dark:bg-pink-900/30 border-2 border-pink-400'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-pink-50 dark:hover:bg-pink-900/20'
                    }`}
                  >
                    <Heart className={`w-6 h-6 transition-all ${formData.addToFavorites ? 'text-pink-500 fill-pink-500 scale-110' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${formData.addToFavorites ? 'text-pink-600 dark:text-pink-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {formData.addToFavorites
                        ? (t.endroits?.inFavorites || 'Dans mes favoris')
                        : (t.endroits?.addToFavorites || 'Ajouter à mes endroits favoris')
                      }
                    </span>
                  </button>

                  {/* Owner declaration - checkbox with disclaimer */}
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, isOwner: !prev.isOwner }))}
                      className={`flex items-center gap-3 w-full p-3 rounded-lg transition-all ${
                        formData.isOwner
                          ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-400'
                          : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      }`}
                    >
                      <Building2 className={`w-6 h-6 transition-all ${formData.isOwner ? 'text-blue-600 scale-110' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${formData.isOwner ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {t.endroits?.iAmOwner || 'Je suis le propriétaire de cet établissement'}
                      </span>
                      {formData.isOwner && (
                        <Check className="w-5 h-5 text-blue-600 ml-auto" />
                      )}
                    </button>
                    {/* Disclaimer */}
                    {formData.isOwner && (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          <strong>Avertissement légal :</strong> En cochant cette case, vous déclarez être le propriétaire ou le représentant légal de cet établissement.
                          Toute fausse déclaration peut constituer une atteinte à l'image de l'entreprise ou un vol de propriété intellectuelle,
                          et vous expose à des poursuites judiciaires.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Options with toggle switches */}
                <div className="flex items-center gap-8">
                  {/* Parking toggle */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="parking"
                        checked={formData.parking}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${formData.parking ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.parking ? 'translate-x-5' : 'translate-x-0'}`} />
                      </div>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t.endroits?.parking || 'Parking disponible'}
                    </span>
                  </label>

                  {/* Status toggle */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="status"
                        checked={formData.status}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${formData.status ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.status ? 'translate-x-5' : 'translate-x-0'}`} />
                      </div>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t.endroits?.active || 'Actif'}
                    </span>
                  </label>
                </div>
              </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title={t.endroits?.deleteTitle || 'Supprimer cet endroit ?'}
        message={`${t.endroits?.deleteMessage || 'Êtes-vous sûr de vouloir supprimer'} "${deleteConfirm?.placeName}" ? ${t.common?.irreversible || 'Cette action est irréversible.'}`}
        confirmText={t.common?.delete || 'Supprimer'}
        confirmVariant="danger"
      />

      {/* Make Public Confirmation */}
      <ConfirmModal
        isOpen={!!makePublicConfirm}
        onClose={() => setMakePublicConfirm(null)}
        onConfirm={() => confirmMakePublic()}
        title="Rendre public cet établissement ?"
        message={`Êtes-vous le propriétaire ou gérant de "${makePublicConfirm?.placeName}" ? En confirmant, cet établissement sera visible publiquement par tous les utilisateurs Cabaza.`}
        confirmText="Oui, je suis propriétaire"
        confirmVariant="primary"
      />

      {/* Duplicate check modal */}
      {showDuplicateModal && (
        <Modal
          isOpen={showDuplicateModal}
          onClose={() => { setShowDuplicateModal(false); setPendingSaveData(null); }}
          title={t.endroits?.searchPublicPlaces || 'Endroit similaire trouve'}
          footer={
            <div className="flex gap-2 justify-end flex-wrap">
              <Button
                variant="secondary"
                onClick={handleCreatePrivateAnyway}
              >
                {t.endroits?.createPrivateInstead || 'Creer en prive quand meme'}
              </Button>
            </div>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t.endroits?.alreadyExistsPublic || 'Un endroit similaire existe deja dans les endroits publics. Voulez-vous le lier a votre profil ?'}
            </p>
            {duplicateCheckResults.map(place => (
              <div
                key={place.placeUuid || place.id}
                className="flex items-center justify-between p-3 rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 cursor-pointer"
                onClick={() => handleLinkExistingPublic(place)}
              >
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-100">{place.placeName}</p>
                  {(place.street || place.city) && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {[place.street, place.number, place.city].filter(Boolean).join(' ')}
                    </p>
                  )}
                </div>
                <span className="text-xs px-2 py-1 bg-emerald-600 text-white rounded-full ml-2 flex-shrink-0">
                  {t.endroits?.linkToMyPlaces || 'Lier'}
                </span>
              </div>
            ))}
          </div>
        </Modal>
      )}

    </div>
  );
};

export default EndroitsPage;
